/**
 * Refact by liuxiangyang on 2025/5/28.
 */
import { RangePicker as RCRangePicker } from '@tinper/rc-picker'
import momentGenerateConfig from '@tinper/rc-picker/lib/generate/moment'
import type { RangeValue } from '@tinper/rc-picker/lib/interface'
import type { RangePickerDateProps } from '@tinper/rc-picker/lib/RangePicker'
import classnames from 'classnames'
import type { Moment } from 'moment'
import moment from 'moment'
import omit from 'omit.js'
import type { FocusEvent } from 'react'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import {
    Warning,
    cssUtil,
    formatUtils,
    getChildrenText,
    getNid,
    prefix,
    setComponentClass,
    timezoneAdaptor
} from '../../wui-core/src'
import Icon from '../../wui-icon/src'
import { globalConfig } from '../../wui-provider/src'
import { autoFormat, canFix, deleteTailSeperator, findMatchIndexs, getDefaultFormat } from './_utils/autoFix'
import { getHalfYear, getHalfYearArr, halfYearFormat } from './_utils/halfYearUtils'
import { getLangInfo, getMomentConfig } from './_utils/lang'
import { getPlacement } from './_utils/placement'
import DatePickerHeaderSelect from './DatePickerHeaderSelect'
import type { RangePickerProps, RangePickerState } from './iPicker'
import { useId } from '../../wui-core/src/reactCompat';

const {isShouldUpdate /* 废弃API调用时提示更新 */} = Warning
export const _PREFIX_CLS = `${prefix}-picker`
const pickers = ['time', 'date', 'week', 'month', 'year']

const _handleProps: (keyof RangePickerProps)[] = [
    'className',
    'size',
    'bordered',
    'align',
    'dropdownAlign',
    'dropdownClassName',
    'popupClassName',
    'getPopupContainer',
    'transitionName',
    'locale',
    'weekStartsOn',
    'timezone',
    'serverTimezone',
    'enableTimezone',
    'format',
    'showTime',
    'use12Hours',
    'value',
    'disabledDateValid',
    'placeholder',
    'mode',
    'ranges',
    'onFocus',
    'onBlur',
    'onChange',
    'onCalendarChange',
    'onOpenChange',
    'onSelect',
    'onStartInputBlur',
    'onEndInputBlur',
    'onPresetChange',
    'activePresetKey',
    'renderExtraFooter',
    'renderFooter'
]

/**
 * @desc 删除日期字符串的label
 * @param {string} dateStr 含label日期字符串
 * @returns string
 */
const _deleteLabel = (dateStr: string) => {
    if (typeof dateStr === 'string') {
        return dateStr.replace(/^\S*\((.*)\)$/g, '$1')
    } else {
        return dateStr
    }
}

const initMode = ({mode}: RangePickerProps): RangePickerState['modeArr'] | undefined => {
    if (mode) {
        return Array.isArray(mode) ? mode : [mode, mode]
    }
}

const initWw = (format: RangePickerProps['format']): boolean => {
    return !!format && /ww|w(?!o)/gi.test(Array.isArray(format) ? format?.[0] : format) // 匹配ww和w，不匹配wo
}

// 修改周格式
const wo2wwFormat = (formatArr: RangePickerState['format'], wo2ww = true): RangePickerState['format'] => {
    return formatArr?.map(item =>
        wo2ww ? item?.replace(/wo/gi, 'ww') : item?.replace(/ww/gi, 'wo')
    ) as RangePickerState['format']
}

/**
* string类型输入转换为数组
* @param {String | Array} str Tinper老版本value、placeholder等的string类型输入
* @param {String} separator 分隔符
* @returns {Array}
*/
const splitRangeStr2Arr = (str: string | RangePickerProps['value'], separator?: string) => {
    if (typeof str !== 'string') return str // 只处理string类型
    let result: string[] = []
    const seperators = separator !== undefined ? [separator, '~', 'to', '至', '-'] : ['~', 'to', '至', '-']
    for (let i = 0; i < seperators.length; i++) {
        if (str.indexOf(seperators[i]) > -1) {
            separator = seperators[i]
            result = str.split(separator)
            break
        }
        if (i === seperators.length) {
            console.error('[param error]: `value` `defaultValue` `placeholder` of RangePicker must has an Array.')
        }
    }
    if (separator !== undefined && str.indexOf(separator) > -1) {
        result = str.split(separator)
    }
    // 日期分隔符与起止分隔符相同情况兼容
    // 如： '2022-01-01 - 2030-05-12'，两者均为 '-'
    if (result.length > 2) {
        result = [result.slice(0, 3).join(separator), result.slice(3).join(separator)]
    }
    // str无分隔符`～`等场景，如 `开始结束`
    if (result.length === 0) {
        result = [str, str]
    }
    return result.map(item => item.trim())
}

const getActivePresetLabel = ({ranges, activePresetKey}: RangePickerProps) => {
    let activePresetLabel = ''
    if (Array.isArray(ranges)) {
        ranges.map(range => {
            if (range.key === activePresetKey || range.label === activePresetKey) {
                activePresetLabel = range.label
            }
        })
    } else if (typeof ranges === 'object') {
        if (activePresetKey && ranges[activePresetKey]) {
            activePresetLabel = activePresetKey
        }
    }
    return activePresetLabel
}

const initRanges = (ranges: RangePickerProps['ranges']) => {
    if (Array.isArray(ranges)) {
        let formatRanges: Record<string, any> = {}
        ranges.map(range => {
            range.label = getChildrenText(range.label).join('')
            formatRanges[range.label] = range.value
        })
        return formatRanges
    } else if (typeof ranges === 'object') {
        return ranges
    } else {
        return {}
    }
}

const getMergedFormat = (format: RangePickerState['format'], initFormat: RangePickerState['initFormat'], labelFormat: RangePickerState['labelFormat']) => {
    if (format && initFormat && format[0] === initFormat[0] && format[1] === initFormat[1]) {
        return labelFormat
    } else {
        return format
    }
}

const RangePicker = React.forwardRef<any, RangePickerProps>(
    (props: RangePickerProps) => {
        const rangeKey = useId()
        const nodeRef = useRef<RCRangePicker<Moment>>(null)
        let leftInputValue: string | number;
        let rightInputValue: string | number;
        let headerSelectLeft: any;
        let headerSelectRight: any;

        const {
            antd,
            id,
            fieldid,
            iconClick,
            picker,
            className,
            size,
            requiredStyle,
            browser,
            readOnly,
            bordered,
            align,
            dropdownClassName,
            popupClassName,
            timezone,
            serverTimezone,
            defaultValue,
            defaultPickerValue,
            linkedPanels = false,
            showRangeLabel,
            superPrevIcon,
            superNextIcon,
            prevIcon,
            nextIcon,
            renderExtraFooter,
            renderFooter,
            dateRender,
            dateCellRender,
            disabledDate,
            disabledDateValid = true,
            atOnceFinish,
            dir: direction = 'ltr',
            isHeaderSelect,
            weekStartsOn,
            locale,
            getPopupContainer,
            getCalendarContainer,
            onOpenChange,
            onPanelChange,
            onFocus,
            onBlur,
            onStartInputBlur,
            onEndInputBlur,
            onChange,
            onCalendarChange,
            onSelect,
            onPresetChange,
        } = props

        let {enableTimezone = true} = props

        if (timezone === 'Asia/Shanghai' && serverTimezone === 'Asia/Shanghai') {
            enableTimezone = false
        }


        /* ---------- ⬇️⬇️⬇️⬇️ state状态 start ⬇️⬇️⬇️⬇️---------- */
        const [langInfo, setLangInfo] = useState(getLangInfo(props))
        const [activePresetLabel, setActivePresetLabel] = useState(getActivePresetLabel(props))
        const [ranges, setRanges] = useState(initRanges(props.ranges))
        const [open, setOpen] = useState(props.open)
        const [modeArr, setModeArr] = useState(initMode(props))
        const [lastSelectDate, setLastSelectDate] = useState<Moment | null>(null)
        const [diffValue, setDiffValue] = useState([0, 0] as RangePickerState['diffValue'])
        const [maskShow, setMaskShow] = useState(false)
        const [rightValue, setRightValue] = useState('')
        const [leftValue, setLeftValue] = useState('')
        const [inputActiveIndex, setInputActiveIndex] = useState(0)
        const [activeSelectPanel, setActiveSelectPanel] = useState('none' as RangePickerState['activeSelectPanel'])

        /* ---------- ⬇️⬇️⬇️⬇️ 函数（不依赖其他函数）逻辑 start ⬇️⬇️⬇️⬇️---------- */

        const generateClearIcon = () => {
            // closeIcon更新为clearIcon，兼容函数式写法
            const {clearIcon, closeIcon} = props
            return (
                <span id={id ? id + '_clear' : undefined} fieldid={fieldid ? fieldid + '_clear' : undefined}>
                    {(typeof clearIcon === 'function' && clearIcon()) ||
                        (typeof closeIcon === 'function' && closeIcon()) ||
                        (closeIcon as ReactNode) ||
                        (clearIcon as ReactNode)}
                </span>
            )
        }

        const generateSuffixIcon = () => {
            // 兼顾 iconClick处理
            const {suffixIcon, renderIcon} = props
            return (
                <span
                    id={id ? id + '_suffix' : undefined}
                    fieldid={fieldid ? fieldid + '_suffix' : undefined}
                    onClick={iconClick}
                >
                    {(typeof suffixIcon === 'function' && suffixIcon()) ||
                        (suffixIcon as ReactNode) ||
                        (typeof renderIcon === 'function' && renderIcon()) ||
                        (renderIcon as ReactNode)}
                </span>
            )
        }

        const initPlaceholder = (props: RangePickerProps): [string, string] | undefined => {
            let {placeholder, dateInputPlaceholder} = props
            placeholder = placeholder || dateInputPlaceholder
            if (!placeholder) return
            if (React.isValidElement(placeholder)) {
                placeholder = getChildrenText(placeholder).join('')
            }
            if (typeof placeholder === 'string') {
                return splitRangeStr2Arr(placeholder) as [string, string]
            } else if (Array.isArray(placeholder)) {
                return [getChildrenText(placeholder[0]).join(''), getChildrenText(placeholder?.[1]).join('')]
            } else {
                console.error('`placeholder` must be an Array.')
            }
        }

        /**
        * @desc 生成RangePicker的format数组
        * @param {String|Array} customFormat
        * @returns [startFormat, endFormat]
        */
        const generateRangeFormat = (customFormat?: RangePickerProps['format']): [string | undefined, string | undefined] => {
            let {format, picker, showTime} = props

            const globalDataFormat = globalConfig().getGlobalDataFormat()
            // format接入工作台首选项
            if (!format && globalDataFormat && (!picker || picker === 'date')) {
                const {dateTimeFormat, dateFormat} = globalDataFormat
                format = formatUtils.diworkFormat2Picker(
                    showTime ? dateTimeFormat : dateFormat,
                    showTime ? 'datetime' : 'date'
                ).format
            }
            const mergeFormat = customFormat || format || getDefaultFormat(picker || 'date', showTime, props)
            // 兼容string、array类型format，并转换为[startFormat, endFormat]数组
            const _result = Array.isArray(mergeFormat)
                ? [mergeFormat[0], mergeFormat?.[1] || mergeFormat[0]]
                : [mergeFormat, mergeFormat]
            return wo2wwFormat(_result as RangePickerState['format'])
        }

        const getPopupContainerDom = (dom: HTMLElement) => {
            if (typeof getPopupContainer === 'function') {
                return getPopupContainer(dom)
            } else if (typeof getCalendarContainer === 'function') {
                return getCalendarContainer(dom)
            } else {
                return cssUtil.parentsUntil(dom)
            }
        }

        // 获取格式后的日期字符串
        const getValue = (value: moment.Moment | string, format: string) => {
            // 此处value可能为string或Moment或空
            if (enableTimezone && moment.isMoment(value)) {
                const {serverUTC, deviceUTC} = timezoneAdaptor({value, format, timezone, serverTimezone})
                value = value.clone().add(serverUTC.secondDiff - deviceUTC.secondDiff, 's') // 用于回调，需要校正为serverTimezone时区时间
            }
            return moment.isMoment(value) ? value.format(format) : value ?? ''
        }

        const handleValue = (val: any) => {
            const yearNum = (val ?? moment()).year()
            const monthNum = (val ?? moment()).month() + 1
            return [yearNum, monthNum]
        }

        const handleInputActiveIndex = (_e: FocusEvent, type: 'start' | 'end') => {
            if (picker === 'time' && type === 'end') {
                setInputActiveIndex(0)
            }
        }

        const onPanelshow = (val: boolean, position: 'left' | 'right') => {
            setActiveSelectPanel(position)
            setMaskShow(!val)
        }

        const handleOpenChange = (open: boolean) => {
            const _diffValue = !open ? [0, 0] as RangePickerState['diffValue'] : diffValue // QDJCJS-27388 修复cascaderChange直接赋值diffValue时，用户可能选择了下拉的select后未选择日期直接关闭面板，导致下次打开面板时显示错误年月，所以每次关闭面板时都清空diffValue
            setOpen(open)
            setDiffValue(_diffValue)
            onOpenChange?.(open)
        }

        /* ---------- ⬇️⬇️⬇️⬇️ 函数逻辑 start ⬇️⬇️⬇️⬇️---------- */
        const generateFormat = (customFormat?: string | string[]) => {
            let {format, showTime} = props

            const globalDataFormat = globalConfig().getGlobalDataFormat()
            // format接入工作台首选项等provider配置
            if (!format && (!picker || picker === 'date') && globalDataFormat) {
                const {dateTimeFormat, dateFormat} = globalDataFormat
                format = formatUtils.diworkFormat2Picker(
                    showTime ? dateTimeFormat : dateFormat,
                    showTime ? 'datetime' : 'date'
                ).format
            }
            // 数组format来源于用户设置的首选项，其中第0项是用户选中值，故format强制取[0]，无需根据list顺序匹配
            customFormat = Array.isArray(customFormat) ? customFormat[0] : customFormat // 兼容string、array类型format
            let mergeFormat = Array.isArray(format) ? format[0] : format // 兼容string、array类型format
            if (picker === 'week' && (customFormat || mergeFormat)) {
                /**
                 * YYYY 格式会导致1月1号等日期所在周年错误，需替换为ISO标准的 g 周年；
                 * 如：2023-01-01 在YYYY-ww 格式下错误输出 2023-52，
                 *                 gggg-ww 格式下正确输出 2023-01
                 */
                customFormat = customFormat?.replace(/y/gi, 'g')
                mergeFormat = mergeFormat?.replace(/y/gi, 'g')
            }
            const _result =
                customFormat ||
                mergeFormat ||
                getDefaultFormat(picker && pickers.includes(picker) ? picker : 'date', showTime, props)
            return initWw(format) ? _result : _result.replace(/W/g, 'w').replace('wo', 'ww')
        }

        const getShowTimeOptions: any = (props: RangePickerProps) => {
            let {format: customFormat, showTime} = props
            if (!showTime) return false
            const format = generateFormat(customFormat as string)

            const globalDataFormat = globalConfig().getGlobalDataFormat()
            // showTime接入工作台首选项等provider配置
            if ((!picker || picker === 'date') && globalDataFormat) {
                const {dateTimeFormat} = globalDataFormat
                const diworkFormat = formatUtils.diworkFormat2Picker(dateTimeFormat, showTime ? 'datetime' : 'date')
                showTime = {...diworkFormat.showTime, ...(typeof showTime === 'object' ? showTime : {})}
            }
            let showTimeOptions: RangePickerProps['showTime'] = {},
                zeroTime = moment('00:00:00', 'HH:mm:ss')
            if (typeof showTime === 'object') {
                // showTime.defaultValue如非moment，则转换为moment
                let {defaultValue, format, use12Hours, hourStep, minuteStep, secondStep} = showTime
                // 为防止用户传入step为0，导致除法报错，不能使用默认值1，需赋值
                hourStep = hourStep ? +hourStep : 1
                minuteStep = minuteStep! ? +minuteStep : 1
                secondStep = secondStep! ? +secondStep : 1
                if (!format) {
                    format = use12Hours ? 'hh:mm:ss a' : 'HH:mm:ss'
                }
                if (defaultValue) {
                    if (typeof defaultValue === 'string') {
                        if (moment(defaultValue, format).isValid()) {
                            defaultValue = moment(defaultValue, format)
                        } else if (!moment(defaultValue, format).isValid()) {
                            defaultValue = zeroTime
                        }
                    } else if (moment.isMoment(defaultValue) && !defaultValue.isValid()) {
                        defaultValue = zeroTime
                    } else if (Array.isArray(defaultValue)) {
                        if (typeof defaultValue[0] === 'string') { // 支持showTime.defaultValue=['00:00:00', '23:00:00']
                            defaultValue[0] = moment(defaultValue[0], format).isValid() ? moment(defaultValue[0], format) : zeroTime
                        }
                        if (typeof defaultValue?.[1] === 'string') {
                            defaultValue[1] = moment(defaultValue[1], format).isValid() ? moment(defaultValue[1], format) : zeroTime
                        }
                    }
                } else {
                    defaultValue = zeroTime // 默认时分秒为 0点0分0秒
                }
                showTimeOptions = {...showTime, defaultValue, hourStep, minuteStep, secondStep}
            } else {
                // 兼容 TimePickerPanel 配置
                showTimeOptions = {
                    format,
                    showHour: format.includes('hh') || format.includes('HH'),
                    showMinute: format.includes('mm'),
                    showSecond: format.includes('ss'),
                    defaultValue: zeroTime // 默认时分秒为 0点0分0秒
                }
            }
            if (showTimeOptions?.use12Hours) {
                showTimeOptions.format = showTimeOptions.format ? showTimeOptions.format.replace(/H/gi, 'h') : 'hh:mm:ss a'
            }
            return showTimeOptions
        }

        const initValue = (props: RangePickerProps): RangeValue<Moment> => {
            const {
                showTime,
                format,
                value: initialValue,
                onChange,
            } = props
            let langInfo = getLangInfo({locale, weekStartsOn}) // 获取语言包信息
            moment.locale(langInfo.lang, getMomentConfig(weekStartsOn))
            const formats = generateRangeFormat(format)
            let valueProp = splitRangeStr2Arr(initialValue) || splitRangeStr2Arr(defaultValue) || []
            let values: (Moment | null)[] = []
            for (let i = 0; i < 2; i++) {
                let value = valueProp[i] || ''
                if (value) {
                    if (typeof value == 'string') {
                        if (picker === 'halfYear') {
                            const halfYearValue = getHalfYear(value, getLangInfo({locale, weekStartsOn})).value
                            values.push(halfYearValue)
                        } else if (moment(value, formats[i]).isValid()) {
                            if (enableTimezone && showTime && (!picker || picker === 'date')) {
                                const {diworkUTC, serverUTC} = timezoneAdaptor({
                                    value,
                                    format: formats[i]!,
                                    timezone,
                                    serverTimezone
                                })
                                value = moment(value, formats[i]).add(diworkUTC.secondDiff - serverUTC.secondDiff, 's')
                            } else {
                                value = moment(value, formats[i])
                            }
                            values.push(value)
                        } else {
                            console.error('value is not in the correct format')
                            let validValue = moment(moment(value), formats[i]) // 如果字符串值和格式生成的moment对象不合法，将字符串的值转换为moment对象，再强制转换一下；
                            // 如 moment('10-13-2222', 'YYYY-MM-DD HH:mm:ss') 非法, 但 moment('10-13-2222') 合法
                            if (validValue.isValid()) {
                                if (enableTimezone && showTime && (!picker || picker === 'date')) {
                                    validValue = timezoneAdaptor({
                                        isCbValue: false,
                                        value: validValue,
                                        format: formats[i]!,
                                        timezone,
                                        serverTimezone
                                    }).value
                                }
                                values.push(validValue)
                            } else {
                                values.push(null)
                            }
                        }
                    } else if (value.isValid?.()) {
                        if (enableTimezone && showTime && (!picker || picker === 'date')) {
                            value = timezoneAdaptor({
                                isCbValue: false,
                                value,
                                format: formats[i]!,
                                timezone,
                                serverTimezone
                            }).value
                        }
                        values.push(value)
                    } else {
                        console.error('value is not in the correct format')
                        values = []
                    }
                } else {
                    values.push(null)
                }
            }

            if (!disabledDateValid) {
                let isDisableStart = values[0] && disabledDate?.(values[0]) // 此处values时区已经过上边判断处理，无需再处理
                let isDisableEnd = values[1] && disabledDate?.(values[1])
                if (isDisableStart || isDisableEnd) {
                    if (isDisableStart) {
                        values[0] = null
                    }
                    if (isDisableEnd) {
                        values[1] = null
                    }
                    onChange?.(values as RangeValue<Moment>, [
                        values[0] === null ? '' : values[0]?.format(formats[0]),
                        values[1] === null ? '' : values[1]?.format(formats[1])
                    ])
                    return values as RangeValue<Moment>
                }
            }

            return values as RangeValue<Moment>
        }

        /* ---------- ⬇️⬇️⬇️⬇️ 原state逻辑依赖函数，需调整到函数声明后 start ⬇️⬇️⬇️⬇️---------- */
        const _initFormats = generateRangeFormat(props.format) as RangePickerState['format'];
        const _initFormat = wo2wwFormat(_initFormats, initWw(props.format));
        const _initShowTime = getShowTimeOptions(props)
        const clearIcon = generateClearIcon()
        const suffixIcon = generateSuffixIcon()
        const dropdownAlign = useMemo(() => getPlacement(props), [props.placement, props.dropdownAlign])

        const [format, setFormat] = useState(_initFormat)
        const [initFormat, setInitFormat] = useState(_initFormat)
        const [labelFormat, setLabelFormat] = useState(_initFormat)
        const [placeholder, setPlaceholder] = useState(initPlaceholder(props))
        // todo: showTime导致re-render死循环了，参考demo5
        const [showTime, setShowTime] = useState(_initShowTime)
        const [value, setValue] = useState(initValue(props))

        let now = moment()
        if (enableTimezone) {
            const {diworkUTC, deviceUTC} = timezoneAdaptor({
                value: now.format(),
                format: 'YYYY-MM-DD HH:mm:ss',
                timezone,
                serverTimezone
            })
            now = now.clone().add(diworkUTC.secondDiff - deviceUTC.secondDiff, 's') // ·此刻·显示为工作台时区
        }

        /* ---------- ⬇️⬇️⬇️⬇️ 回调函数 start ⬇️⬇️⬇️⬇️---------- */
        const handleDisabledDate = (current: moment.Moment) => {
            if (!disabledDate) return false
            if (!picker || picker === 'date') {
                if (!showTime) {
                    current = current?.startOf('date')
                } else if (enableTimezone) {
                    current = timezoneAdaptor({
                        value: current,
                        format: initFormat[0],
                        timezone,
                        serverTimezone
                    }).value
                }
            }
            return disabledDate?.(current)
        }

        // 处理下拉面板解耦参数更新不及时问题
        const asyncUpdateActiveSelectPanel = () =>
            requestAnimationFrame(() => {
                setActiveSelectPanel('none')
            })

        // 下拉面板隐藏时activeSelectPanel更新为none
        const updateActiveSelectPanel = (flag: string) => {
            if (!picker || picker === 'date' || picker === 'week') {
                if (flag == 'monthFlag') {
                    asyncUpdateActiveSelectPanel()
                }
            } else {
                asyncUpdateActiveSelectPanel()
            }
        }

        const getDiffValueHandle = ({
            value,
            val,
            ismask,
            position,
            flag
        }: {
            value: [number, number]
            val: string
            ismask: boolean
            position: 'left' | 'right'
            flag: string
        }) => {
            // 选择下拉之后更新面板
            if (!picker || picker === 'date' || picker === 'week') {
                let dateArr = val.split(/\s+/)
                let newRightValue = dateArr[0] + ' ' + (parseInt(dateArr[1]) + 1) + '月'
                setLeftValue(position === 'left' ? newRightValue : val)
                setRightValue(position === 'left' ? val : newRightValue)
            } else if (picker === 'month' || picker === 'quarter' || picker === 'halfYear') {
                let newRightValue = parseInt(val) + '年'
                setLeftValue(position === 'left' ? newRightValue : parseInt(val) - 1 + '年')
                setRightValue(position === 'left' ? parseInt(val) + 1 + '年' : newRightValue)
            } else if (picker === 'year') {
                let newArr = val.split('-')
                setLeftValue(position === 'left' ? val : parseInt(newArr[0]) - 10 + '-' + (parseInt(newArr[1]) - 10))
                setRightValue(position === 'left' ? parseInt(newArr[0]) + 10 + '-' + (parseInt(newArr[1]) + 10) : val)
            }
            updateActiveSelectPanel(flag)
            setDiffValue(value)
            setMaskShow(ismask)
        }

        const hiddenHandleSelect = (position: 'left' | 'right') => {
            if (position === 'left') {
                headerSelectRight?.inputBlur(false)
            } else {
                headerSelectLeft?.inputBlur(false)
            }
            asyncUpdateActiveSelectPanel()
        }

        const generateSelectPanel = (position: 'left' | 'right') => {
            return (
                <DatePickerHeaderSelect
                    {...(position === 'left' ? {valueLeft: leftValue} : {value: rightValue})}
                    fieldid={fieldid}
                    key={position}
                    position={position}
                    direction={direction}
                    lang={langInfo}
                    maskShow={maskShow}
                    onPanelshow={onPanelshow}
                    picker={picker}
                    now={now}
                    getDiffValueHandle={getDiffValueHandle}
                    defaultValue={defaultValue}
                    ref={el => (position === 'left' ? (headerSelectLeft = el) : (headerSelectRight = el))}
                    hiddenHandleSelect={hiddenHandleSelect}
                ></DatePickerHeaderSelect>
            )
        }

        const onInputBlur = (e: FocusEvent<Element, Element>, type: 'start' | 'end') => {
            const format = wo2wwFormat(initFormat as RangePickerState['format'], initWw(props.format))
            if (!disabledDateValid && value && disabledDate) {
                if (value[0] && disabledDate(value[0])) {
                    value[0] = null
                }
                if (value[1] && disabledDate(value[1])) {
                    value[1] = null
                }
            }
            let startValue = value?.[0]?.format(format[0]) ?? ''
            let endValue = value?.[1]?.format(format[1]) ?? ''

            if (enableTimezone && showTime && (!picker || picker === 'date')) {
                for (let i = 0; i < 2; i++) {
                    if (value?.[i]) {
                        const timezoneValue = timezoneAdaptor({
                            value: value[i]!,
                            format: format[i],
                            timezone,
                            serverTimezone
                        }).value
                        const timezoneValueStr = getValue(timezoneValue, format[i])
                        i === 0 ? (startValue = timezoneValueStr) : i === 1 ? (endValue = timezoneValueStr) : ''
                    }
                }
            }

            // QDJCJS-9244，删除到空视为有效输入。解决手动清除结束日期后，起始日期无法选择被清除的结束日期以后日期的问题
            if ((e.target as any).value === '') {
                let _value
                if (type === 'start' && value?.[0] !== null) {
                    _value = [null, value?.[1] || null] as RangePickerState['value']
                    // 有效输入需触发值变更、onChange回调
                    setValue(_value)
                    if (picker === 'halfYear') {
                        const halfYearArr = getHalfYearArr(langInfo, endValue)
                        onChange?.(_value, `["" , "${endValue}"]`, ['', endValue], [['', ''], halfYearArr])
                    } else {
                        if (enableTimezone && _value?.[1] && showTime && (!picker || picker === 'date')) {
                            _value[1] = timezoneAdaptor({
                                value: _value[1],
                                format: format[1],
                                timezone,
                                serverTimezone
                            }).value
                            endValue = getValue(_value[1], format[1])
                        }
                        onChange?.(_value, `["" , "${endValue}"]`, ['', endValue])
                    }
                } else if (type === 'end' && value?.[1] !== null) {
                    _value = [value?.[0] || null, null] as RangePickerState['value']
                    // 有效输入需触发值变更、onChange回调
                    setValue(_value)
                    if (picker === 'halfYear') {
                        const halfYearArr = getHalfYearArr(langInfo, startValue)
                        onChange?.(_value, `["${startValue}", ""]`, [startValue, ''], [halfYearArr, ['', '']])
                    } else {
                        if (enableTimezone && _value?.[0] && showTime && (!picker || picker === 'date')) {
                            _value[0] = timezoneAdaptor({
                                value: _value[0],
                                format: format[0],
                                timezone,
                                serverTimezone
                            }).value
                            startValue = getValue(_value[0], format[0])
                        }
                        onChange?.(_value, `["${startValue}", ""]`, [startValue, ''])
                    }
                }
            }
            // 失去焦点时下拉收起
            headerSelectLeft?.inputBlur(false)
            headerSelectRight?.inputBlur(false)
            setMaskShow(false)

            type === 'start'
                ? onStartInputBlur?.(e, startValue, [startValue, endValue])
                : onEndInputBlur?.(e, endValue, [startValue, endValue])
        }

        // focus时，如有label，需移除，便于用户修改
        const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
            const _format = wo2wwFormat(initFormat as RangePickerState['format'])
            setFormat(_format)
            if (readOnly) return
            if (showRangeLabel) {
                setLabelFormat(_format)
                onFocus?.(e)
            } else {
                onFocus?.(e)
            }
        }

        // blur时，如有showRangeLabel，需添加
        const handleBlur = (e: FocusEvent, type: 'start' | 'end') => {
            onInputBlur?.(e, type)
            const _format = wo2wwFormat(initFormat as RangePickerState['format'], initWw(props.format))
            const _labelFormat = wo2wwFormat(labelFormat as RangePickerState['format'], initWw(props.format))
            if (showRangeLabel) {
                const presetFormat: [string, string] =
                    showRangeLabel && activePresetLabel
                        ? [activePresetLabel + '(' + _format[0] + ')', activePresetLabel + '(' + _format[1] + ')']
                        : _labelFormat
                setLabelFormat(presetFormat)
            } else {
                setLabelFormat(_labelFormat)
            }
            onBlur?.(e)
        }

        /**
         * 日期范围变化的回调
         * @param {Array[moment]} value 选中的日期范围值
         * @param {Array[String]} formatString 数组字符串
         * @param {[String]} label 快捷按钮文本，非快捷按钮选中日期无此项
         */
        const handleChange = (value: RangePickerState['value'], formatString: [string, string], label?: string) => {
            formatString = [_deleteLabel(formatString[0]), _deleteLabel(formatString[1])]
            if (antd) {
                // 兼容antd参数
                setValue(value)
                onChange?.(value, formatString)
            } else {
                if (!label) {
                    setValue(value)
                    setLabelFormat(initFormat)
                    setActivePresetLabel('')
                } else {
                    const presetFormat: [string, string] = showRangeLabel
                        ? [label + '(' + initFormat[0] + ')', label + '(' + initFormat[1] + ')']
                        : initFormat
                    setValue(value)
                    setLabelFormat(presetFormat)
                }
                /**
                 * 1. 保持与4.2.0重构前版本一致，在change事件前触发onPanelChange，以确保start/end均已取到
                 * 2. 增加第二个参数mode
                 * 3. clear的空值不触发
                 */
                if (value) {
                    if (picker === 'halfYear') {
                        const halfYearStartArr = getHalfYearArr(langInfo, formatString?.[0])
                        const halfYearEndArr = getHalfYearArr(langInfo, formatString?.[1])

                        flushSync(() => {
                            onPanelChange?.(value, modeArr)
                            onChange?.(value, `["${formatString?.[0]}" , "${formatString?.[1]}"]`, formatString, [
                                halfYearStartArr,
                                halfYearEndArr
                            ])
                        })
                    } else {
                        for (let i = 0; i < 2; i++) {
                            if (enableTimezone && value?.[i] && showTime && (!picker || picker === 'date')) {
                                value[i] = timezoneAdaptor({
                                    value: value[i]!,
                                    format: initFormat[i],
                                    timezone,
                                    serverTimezone
                                }).value
                                formatString[i] = getValue(value[i]!, initFormat[i])
                            }
                        }
                        flushSync(() => {
                            onPanelChange?.(value, modeArr)
                            // 老版本参数为三个，需保证新旧版本参数一致
                            onChange?.(value, `["${formatString?.[0]}" , "${formatString?.[1]}"]`, formatString)
                        })
                    }
                } else {
                    setDiffValue([0, 0])
                    setLeftValue('')
                    setRightValue('')
                    flushSync(() => {
                        // 老版本参数为三个，需保证新旧版本参数一致
                        onChange?.(value, '', null)
                    })
                }
            }
        }

        const handleCalendarChange = (
            value: RangePickerState['value'],
            formatString: [string, string],
            info: {range: string}
        ) => {
            formatString = [_deleteLabel(formatString[0]), _deleteLabel(formatString[1])]
            // 记录上次用户选择日期，用于快捷按钮点击时的回调参数
            if (value === null) {
                // clear事件
                setLastSelectDate(null)
            } else {
                setLastSelectDate(info.range === 'start' ? value?.[0] : value?.[1])
            }

            for (let i = 0; i < 2; i++) {
                if (enableTimezone && value?.[i] && showTime && (!picker || picker === 'date')) {
                    value[i] = timezoneAdaptor({
                        value: value[i]!,
                        format: initFormat[i],
                        timezone,
                        serverTimezone
                    }).value
                    formatString[i] = getValue(value[i]!, initFormat[i])
                }
            }

            onCalendarChange?.(value, formatString, info)

            if (info.range === 'start') {
                onSelect?.(value?.[0] as moment.Moment)
            } else if (info.range === 'end') {
                onSelect?.(value?.[1] as moment.Moment)
                if (value && !value?.includes(null) && showTime && atOnceFinish) { // 存在时间面板如果开始结束都存在值，则操作结束日期，如果结束日期大于开始日期则直接关闭面板
                    let isBefore = moment(value![0]).isBefore(value![1])
                    if (isBefore) {
                        setOpen(false)
                    }
                }
            }
        }

        const handleInputFocus = (val: number, val2: string, e: FocusEvent<HTMLInputElement>) => {
            // 左右input触发回调
            handleInputActiveIndex(e, val === 0 ? 'start' : 'end')
            handleFocus(e)
            if (open && isHeaderSelect) {
                if (val2 && val === 0) {
                    // 触发左面板赋值
                    if (!picker || picker === 'date' || picker === 'week') {
                        let dateArr = handleValue(value?.[0])
                        leftInputValue = dateArr![0] + dateArr![1]
                        setLeftValue(dateArr![0] + '年 ' + dateArr![1] + '月')
                        setRightValue(dateArr![0] + '年 ' + (+dateArr![1] + 1) + '月')
                    } else if (picker === 'month' || picker === 'quarter' || picker === 'halfYear') {
                        leftInputValue = +val2
                        setRightValue(+val2 + 1 + '年')
                        setLeftValue(val2 + '年')
                    } else if (picker === 'year') {
                        let yearNum = +val2
                        let currentYear = yearNum - +yearNum.toString().slice(3, 4)
                        setRightValue(currentYear + 10 + '-' + (currentYear + 19))
                        setLeftValue(currentYear + '-' + (currentYear + 9))
                    }
                }
                if (val2 && val === 1) {
                    // 触发右面板赋值
                    if (!picker || picker === 'date' || picker === 'week') {
                        let dateArr = handleValue(value?.[1])
                        rightInputValue = dateArr![0] + dateArr![1]
                        if (rightInputValue !== leftInputValue) {
                            setRightValue(dateArr![0] + '年 ' + (dateArr![1] - 1) + '月')
                            setLeftValue(dateArr![0] + '年 ' + dateArr![1] + '月')
                        }
                    } else if (picker === 'month' || picker === 'quarter' || picker === 'halfYear') {
                        rightInputValue = +val2
                        if (rightInputValue !== leftInputValue) {
                            setRightValue(val2)
                            setLeftValue(+val2 - 1 + '年')
                        }
                    } else if (picker === 'year') {
                        let yearNum = +val2
                        let currentYear = yearNum - +yearNum.toString().slice(3, 4)
                        setRightValue(currentYear + '-' + (currentYear + 9))
                        setLeftValue(currentYear - 10 + '-' + (currentYear - 19))
                    }
                }
                setInputActiveIndex(val)
            }
        }

        // 点击翻页拉下值更新
        const handlePanelChange = (
            value: RangePickerState['value'],
            mode: RangePickerState['modeArr'],
            type?: string,
            diff?: number
        ) => {
            setModeArr(mode)
            antd && onPanelChange?.(value, mode)
            if (!isHeaderSelect || !open) return
            if (!picker || picker === 'date' || picker === 'week') {
                if (type == 'year' && (diff as number) > 0) {
                    // 年度右翻页
                    let firstValue = ''
                    let lastmonth = ''
                    let lastValue = ''
                    if (inputActiveIndex === 0) {
                        // 左面的input触发
                        firstValue = (value![0] || value![1])?.year() + '年 ' + (value![0] || value![1])?.month() + '月'
                        lastmonth = (value![0] || value![1])!?.month() + 1 + '月'
                        lastValue = (value![0] || value![1])?.year() + '年 ' + lastmonth
                    } else {
                        firstValue = (value![1] || value![0])?.year() + '年 ' + (value![1] || value![0])?.month() + '月'
                        lastmonth = (value![1] || value![0])!?.month() + 1 + '月'
                        lastValue = (value![1] || value![0])?.year() + '年 ' + lastmonth
                    }
                    setRightValue(firstValue)
                    setLeftValue(lastValue)
                } else if (type == 'year' && (diff as number) < 0) {
                    // 年度左翻页
                    let firstValue = ''
                    let lastValue = ''
                    if (inputActiveIndex === 0) {
                        firstValue =
                            (value![0] || value![1])?.year() + '年 ' + ((value![0] || value![1])!?.month() + 1) + '月'
                        lastValue =
                            (value![0] || value![1])?.year() + '年 ' + ((value![0] || value![1])!?.month() + 2) + '月'
                    } else {
                        firstValue =
                            (value![1] || value![0])?.year() + '年 ' + ((value![1] || value![0])!?.month() + 1) + '月'
                        lastValue =
                            (value![1] || value![0])?.year() + '年 ' + ((value![1] || value![0])!?.month() + 2) + '月'
                    }
                    setRightValue(firstValue)
                    setLeftValue(lastValue)
                } else if (type == 'month' && (diff as number) > 0) {
                    // 月度右翻页
                    let firstValue = ''
                    let lastValue = ''
                    if (inputActiveIndex === 0) {
                        firstValue = value![0]?.year() + '年 ' + value![0]?.month() + '月'
                        lastValue = value![0]?.year() + '年 ' + (value![0]!?.month() + 1) + '月'
                    } else {
                        firstValue = value![1]?.year() + '年 ' + value![1]?.month() + '月'
                        lastValue = value![1]?.year() + '年 ' + ((value![1] || value![0])!?.month() + 1) + '月'
                    }
                    setRightValue(firstValue)
                    setLeftValue(lastValue)
                } else if (type == 'month' && (diff as number) < 0) {
                    // 月度左翻页
                    let firstValue = value![0]?.year() + '年 ' + value![0]?.month() + '月'
                    let lastValue =
                        (value![1]?.year() || value![0]?.year()) + '年 ' + ((value![0] || value![1])!?.month() + 1) + '月'
                    if (inputActiveIndex === 0) {
                        firstValue = value![0]?.year() + '年 ' + (value![0]!?.month() + 1) + '月'
                        lastValue =
                            (value![0]?.year() || value![1]?.year()) +
                            '年 ' +
                            ((value![0] || value![1])!?.month() + 2) +
                            '月'
                    } else {
                        firstValue = value![1]?.year() + '年 ' + (value![1]!?.month() + 1) + '月'
                        lastValue = value![1]?.year() + '年 ' + ((value![1] || value![0])!?.month() + 2) + '月'
                    }
                    setRightValue(firstValue)
                    setLeftValue(lastValue)
                }
            } else if (picker === 'month' || picker === 'quarter' || picker === 'halfYear') {
                if (type == 'year' && (diff as number) > 0) {
                    let firstValue = ''
                    let lastValue = ''
                    if (inputActiveIndex === 0) {
                        // 左面的input触发
                        firstValue = (value![0] || value![1])?.year() + '年 '
                        lastValue = (value![0] || value![1])!?.year() - 1 + '年 '
                    } else {
                        firstValue = (value![1] || value![0])?.year() + '年 '
                        lastValue = (value![1] || value![0])!?.year() - 1 + '年 '
                    }
                    setRightValue(firstValue)
                    setLeftValue(lastValue)
                } else if (type == 'year' && (diff as number) < 0) {
                    let firstValue = ''
                    let lastValue = ''
                    if (inputActiveIndex === 0) {
                        firstValue = (value![0] || value![1])!?.year() + '年 '
                        lastValue = (value![0] || value![1])!?.year() - 1 + '年 '
                    } else {
                        firstValue = (value![1] || value![0])!?.year() + '年 '
                        lastValue = (value![1] || value![0])!?.year() - 1 + '年 '
                    }
                    setRightValue(firstValue)
                    setLeftValue(lastValue)
                }
            } else if (picker === 'year') {
                if (type == 'year' && (diff as number) > 0) {
                    let firstValue = ''
                    let lastValue = ''
                    if (inputActiveIndex === 0) {
                        // 左面的input触发
                        let yearNum = (value![0] || value![1])?.year()
                        let currentYear = (yearNum as number) - parseInt((yearNum as number).toString().slice(3, 4))
                        firstValue = currentYear + '-' + (currentYear + 9)
                        lastValue = currentYear - 10 + '-' + (currentYear - 1)
                    } else {
                        let yearNum = (value![1] || value![0])?.year()
                        let currentYear = (yearNum as number) - parseInt((yearNum as number).toString().slice(3, 4))
                        firstValue = currentYear + '-' + (currentYear + 9)
                        lastValue = currentYear - 10 + '-' + (currentYear - 1)
                    }
                    setRightValue(firstValue)
                    setLeftValue(lastValue)
                } else if (type == 'year' && (diff as number) < 0) {
                    let firstValue = ''
                    let lastValue = ''
                    if (inputActiveIndex === 0) {
                        // 左面的input触发
                        let yearNum = (value![0] || value![1])?.year()
                        let currentYear = (yearNum as number) - parseInt((yearNum as number).toString().slice(3, 4))
                        firstValue = currentYear + '-' + (currentYear + 9)
                        lastValue = currentYear - 10 + '-' + (currentYear - 1)
                    } else {
                        let yearNum = (value![1] || value![0])?.year()
                        let currentYear = (yearNum as number) - parseInt((yearNum as number).toString().slice(3, 4))
                        firstValue = currentYear + '-' + (currentYear + 9)
                        lastValue = currentYear - 10 + '-' + (currentYear - 1)
                    }
                    setRightValue(firstValue)
                    setLeftValue(lastValue)
                }
            }
        }

        /**
         * @desc 选中快捷日期的回调
         * @param {String} label 快捷选项文本
         * @param {[moment||null, moment||null]} value 新选中的值
         */
        const handlePresetChange = (label: string, value: RangeValue<Moment>) => {
            const { ranges } = props
            for (let i = 0; i < 2; i++) {
                if (enableTimezone && value?.[i] && showTime && (!picker || picker === 'date')) {
                    value[i] = timezoneAdaptor({
                        value: value[i]!,
                        format: initFormat[i],
                        timezone,
                        serverTimezone
                    }).value
                }
            }

            if (Array.isArray(ranges)) {
                let item = ranges.filter(range => range.label === label)[0]
                setActivePresetLabel(label)
                onPresetChange?.(label, value, item, lastSelectDate)
            } else if (typeof ranges === 'object') {
                setActivePresetLabel(label)
                onPresetChange?.(label, value, ranges[label], lastSelectDate)
            }
            // 快捷键选中日期范围后触发失焦
            const inputs = document?.querySelectorAll?.(`div[domKey="${nodeRef.current?.props?.domKey}"] .${_PREFIX_CLS}-input input`) as NodeListOf<HTMLInputElement>
            inputs?.[0]?.blur()
            inputs?.[1]?.blur()
        }

        /**
         * 格式化Input输入内容：自动填充
         *  =========== input输入字符串视为diwork时区字符串（与props字符串视为server时区不同），回调将调整为server时区 ============
         */
        const handleInputChange = (e: any, type: 'start' | 'end') => {
            const [startFormat, endFormat] = initFormat!
            let format = type === 'start' ? startFormat : endFormat
            const _isFix = canFix(props, format)
            const stateValue = value
            // * 开启自动校正
            if (_isFix) {
                let value = e.target.value,
                    isDeleteChanged = false
                if (!/^\d/g.test(value)) { // 非数字开头视为非法输入，不做处理
                    return
                }
                const hasSeparator = /[^YyMmDdHhSsGgWwOoAa]+/g.test(format) // 判断是否有 - / . 年月日、空格等分隔符
                const {joinIcon, addLength} = findMatchIndexs(format, hasSeparator)
                if (hasSeparator && joinIcon?.[0] !== '') {
                    if (/^\d{5}$/.test(value)) {
                        // 处理删除尾部补足的分隔符-后再输入数字时只填充分隔符-而没数字问题
                        if (addLength?.[0]) {
                            value = value.slice(0, addLength[0]) + joinIcon[0] + value.slice(addLength[0])
                            isDeleteChanged = true
                        }
                    } else if (value.search(/\d{5,}/) > -1) {
                        // 处理日期中间删除问题
                        value =
                            e.target.value.slice(0, Math.max(0, e.target.selectionStart - 1)) +
                            e.target.value.slice(e.target.selectionStart)
                        isDeleteChanged = true
                    }
                }
                const formatDate = autoFormat({value, format, showTime, locale: langInfo.lang})
                let valueStr = formatDate.value
                if (e.inputType === 'deleteContentBackward') {
                    if (value.length === 0) {
                        // 删除到空时需触发onChange，并关闭面板触发onOpenChange
                        setValue(null)
                        setFormat(initFormat)
                        onChange?.(null, `['', '']`, ['', ''])
                        return
                    }
                    // 删除操作尾部分隔符处理
                    Object.assign(formatDate, deleteTailSeperator(valueStr, formatDate.format))
                }

                if (formatDate.isChanged || isDeleteChanged) {
                    if (/\D0$/g.test(valueStr)) {
                        /* 尾部的-0特别处理，format替换为-0 */
                        let tempFormat = formatDate.format.slice(0, -2) + valueStr.slice(-2)
                        const currentValue = valueStr ? moment(valueStr, tempFormat) : null;
                        const valueArr = type === 'start' ? [currentValue, stateValue?.[1]] : [stateValue?.[0], currentValue]
                        const formatArr = type === 'start' ? [tempFormat, endFormat] : [startFormat, tempFormat]
                        setValue(valueArr as RangeValue<Moment>)
                        setFormat(formatArr as RangePickerState['format'])
                    } else if (!/[^S]+/g.test(formatDate.format)) {
                        /* 只有年的特别处理，Y被转S替换，禁止用户非数字输入 */
                        const currentValue = valueStr ? moment(valueStr.replace(/\D+/g, ''), formatDate.format) : null;
                        const valueArr = type === 'start' ? [currentValue, stateValue?.[1]] : [stateValue?.[0], currentValue]
                        const formatArr = type === 'start' ? [formatDate.format, endFormat] : [startFormat, formatDate.format]
                        setValue(valueArr as RangeValue<Moment>)
                        setFormat(formatArr as RangePickerState['format'])
                    } else {
                        const currentValue = valueStr ? moment(valueStr, formatDate.format) : null;
                        const valueArr = type === 'start' ? [currentValue, stateValue?.[1]] : [stateValue?.[0], currentValue]
                        const formatArr = type === 'start' ? [formatDate.format, endFormat] : [startFormat, formatDate.format]
                        setValue(valueArr as RangeValue<Moment>)
                        setFormat(formatArr as RangePickerState['format'])
                    }
                }

                let valueMoment = moment(valueStr, formatDate.format)
                if (
                    // 输入完成
                    format?.length === valueStr?.length &&
                    valueMoment.isValid()
                ) {
                    const currentValue: RangeValue<Moment> = type === 'start' ? [valueMoment, stateValue?.[1] ?? null] : [stateValue?.[0] ?? null, valueMoment]

                    if (enableTimezone && showTime && (!picker || picker === 'date')) {
                        valueMoment = timezoneAdaptor({
                            value: valueMoment,
                            format,
                            timezone,
                            serverTimezone
                        }).value
                        valueStr = getValue(valueMoment, format) || ''
                    }
                    let startValueStr = '', endValueStr = '';
                    if (type === 'start') {
                        startValueStr = valueStr
                        endValueStr = currentValue?.[1] ? getValue(currentValue?.[1], format) : ''
                    } else {
                        startValueStr = currentValue?.[0] ? getValue(currentValue?.[0], format) : ''
                        endValueStr = valueStr
                    }

                    requestAnimationFrame(() => { // fix: QDJCJS-27275 解决面板selectedValue未随输入框值startText/endText变化导致自动化测试阻塞问题
                        setValue(currentValue)
                        setFormat(initFormat)
                        onChange?.(currentValue, `[${startValueStr}, ${endValueStr}]`, [startValueStr, endValueStr])
                    })
                }
            } else {
                // * 无需自动校正场景，仅保留合法值
                let inputValue = e.target.value
                let valueMoment = moment(inputValue, format)

                // halfYear 单独处理回调及相关dom事件
                if (inputValue.length === format.length && picker === 'halfYear') {
                    requestAnimationFrame(() => {
                        const {value, dateString} = getHalfYear(inputValue, langInfo)
                        const currentValue: RangeValue<Moment> = type === 'start' ? [value, stateValue?.[1] ?? null] : [stateValue?.[0] ?? null, value]
                        setValue(currentValue)
                        let startValueStr = '', endValueStr = '';
                        if (type === 'start') {
                            startValueStr = dateString
                            endValueStr = currentValue?.[1] ? getValue(currentValue?.[1], format) : ''
                        } else {
                            startValueStr = currentValue?.[0] ? getValue(currentValue?.[0], format) : ''
                            endValueStr = dateString
                        }
                        onChange?.(currentValue, `[${startValueStr}, ${endValueStr}]`, [startValueStr, endValueStr])
                    })
                } else if (inputValue.length === format.length && valueMoment.isValid()) {
                    requestAnimationFrame(() => {
                        const currentValue: RangeValue<Moment> = type === 'start' ? [valueMoment, stateValue?.[1] ?? null] : [stateValue?.[0] ?? null, valueMoment]
                        setValue(currentValue)
                        setFormat(initFormat)

                        if (enableTimezone && showTime && (!picker || picker === 'date')) {
                            valueMoment = timezoneAdaptor({
                                value: valueMoment,
                                format,
                                timezone,
                                serverTimezone
                            }).value
                            inputValue = getValue(valueMoment, format) || ''
                        }
                        const _currentValue: RangeValue<Moment> = type === 'start' ? [valueMoment, stateValue?.[1] ?? null] : [stateValue?.[0] ?? null, valueMoment]
                        let startValueStr = '', endValueStr = '';
                        if (type === 'start') {
                            startValueStr = inputValue
                            endValueStr = _currentValue?.[1] ? getValue(_currentValue?.[1], format) : ''
                        } else {
                            startValueStr = _currentValue?.[0] ? getValue(_currentValue?.[0], format) : ''
                            endValueStr = inputValue
                        }
                        onChange?.(currentValue, `[${startValueStr}, ${endValueStr}]`, [startValueStr, endValueStr])
                    })
                }
            }
        }

        const value2Str = (value: any, format: any) => {
            let dateString = '--'
            if (value && value.isValid()) {
                if (picker === 'halfYear') {
                    dateString = halfYearFormat(value, langInfo, true)
                } else {
                    dateString = value.format(format)
                }
            } else {
                dateString = value?.toString()
            }
            const displayText = dateString || '--'; // 如果值为空，显示 '--'
            return displayText
        }

        // 浏览态
        const renderBrowser = (params: any) => {
            let {browserClassName, browserStyle, bordered, value, format} = params;

            const componentCls = classnames(`${_PREFIX_CLS}-browser`, {
                [browserClassName]: browserClassName,
                [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom'
            })

            let displayText = '--'
            const separator = props.separator || ' ~ '

            if (Array.isArray(value)) {
                displayText = value2Str(value[0], format[0]) + separator + value2Str(value?.[1], format[1])
            }

            return (
                <div className={`${prefix}-component-browser`}>
                    <div className={componentCls} style={browserStyle} fieldid={fieldid}>
                        {displayText}
                    </div>
                </div>
            );
        }

        /* ---------- ⬇️⬇️⬇️⬇️ 钩子函数 start ⬇️⬇️⬇️⬇️---------- */
        useEffect(() => {
            isShouldUpdate('DatePicker', props);

            if ('value' in props) {
                setValue(initValue(props))
            }
            if (typeof props.open === 'boolean') {
                setOpen(props.open)
            }
            if (props.format) {
                const format = generateRangeFormat(props.format)
                if (format[0] && format?.[1]) {
                    setFormat(format as RangePickerState['format'])
                    setInitFormat(format as RangePickerState['format'])
                    setLabelFormat(format as RangePickerState['format'])
                }
            }
            if (props.showTime !== undefined) {
                setShowTime(getShowTimeOptions(props))
            }
            if (props.mode) {
                setModeArr(initMode(props))
            }
            if (props.placeholder || props.dateInputPlaceholder) {
                setPlaceholder(initPlaceholder(props))
            }
            if (props.ranges) {
                setRanges(initRanges(props.ranges))
            }
            if (props.activePresetKey) {
                setActivePresetLabel(getActivePresetLabel(props))
            }
            if (props.locale) {
                const lang = getLangInfo(props) // 获取语言包信息
                setLangInfo(lang)
            }
        }, [props.value, props.defaultValue, props.format, props.open, props.showTime, props.mode, props.placeholder, props.dateInputPlaceholder, props.ranges, props.locale, props.activePresetKey, props.presets]);

        /* ---------- ⬇️⬇️⬇️⬇️ 原 render逻辑 start ⬇️⬇️⬇️⬇️---------- */
        const _defaultPickerValue = (!value && !defaultValue && defaultPickerValue) || undefined

        const leftPanel = open && isHeaderSelect ? generateSelectPanel('left') : null
        const rightPanel = open && isHeaderSelect ? generateSelectPanel('right') : null

        const mergedFormat = getMergedFormat(format, initFormat, labelFormat)

        const mergedCls = useMemo(() => classnames(className, size && `${_PREFIX_CLS}-${size}`, {
            ...setComponentClass({clsPrefix: _PREFIX_CLS, bordered, align, requiredStyle, readOnly})
        }), [className, size, bordered, align, requiredStyle, readOnly])
        const globalDataFormat = globalConfig().getGlobalDataFormat()
        let use12Hours = props.use12Hours
        if ((!picker || picker === 'date') && showTime) {
            if (globalDataFormat) {
                const {dateTimeFormat} = globalDataFormat
                if (use12Hours === undefined) {
                    use12Hours = formatUtils.diworkFormat2Picker(dateTimeFormat, 'datetime').use12Hours
                }
            }
        }

        if (browser) {
            return renderBrowser({size, className, bordered, align, requiredStyle, value: value !== undefined ? value : defaultValue, format: mergedFormat, picker})
        }

        const generateConfig = useMemo(() => Object.assign(momentGenerateConfig, {getNow: () => now}), [now]) // 工作台与计算机时区不一致场景的时间调整
        const adapterNid = getNid(props)

        return (
            <RCRangePicker
                {...adapterNid}
                {...omit(props, _handleProps)}
                /* 内置配置 */
                ref={nodeRef}
                direction={direction}
                prefixCls={_PREFIX_CLS}
                // transitionName='slide-up'
                className={mergedCls}
                dropdownClassName={dropdownClassName || popupClassName}
                dropdownAlign={dropdownAlign}
                generateConfig={generateConfig}
                locale={Object.assign({}, langInfo.langMap, {
                    shortMonths: langInfo.langMap.monthsShort,
                    // @ts-ignore
                    shortWeekDays: langInfo.langMap.weekdaysMin,
                    halfYearFormat: (date: Moment) => halfYearFormat(date, langInfo, false)
                })} // 语言包必须提供
                defaultPickerValue={_defaultPickerValue} // 无value且无defaultValue时才可使用defaultPickerValue
                value={value ?? undefined}
                format={picker === 'halfYear' ? (date: Moment) => halfYearFormat(date, langInfo) : mergedFormat}
                use12Hours={use12Hours}
                showTime={showTime as RangePickerDateProps<Moment>['showTime']}
                open={open ?? undefined}
                mode={modeArr}
                placeholder={placeholder}
                ranges={ranges}
                activePresetLabel={activePresetLabel}
                linkedPanels={linkedPanels}
                disabledDate={handleDisabledDate}
                onInputFocus={handleInputFocus}
                onInputChange={handleInputChange}
                onBlur={handleBlur}
                onPresetChange={handlePresetChange}
                onChange={handleChange}
                onCalendarChange={handleCalendarChange}
                onOpenChange={handleOpenChange}
                onPanelChange={handlePanelChange}
                getPopupContainer={getPopupContainerDom as (node: HTMLElement) => HTMLElement}
                renderExtraFooter={renderExtraFooter || renderFooter}
                dateRender={dateRender || dateCellRender}
                clearIcon={clearIcon}
                suffixIcon={suffixIcon}
                superPrevIcon={superPrevIcon ?? <Icon type={direction === 'rtl' ? 'uf-youjiantou_shuang' : 'uf-daoshouye'} />} // UE 需求，更换默认翻页箭头
                superNextIcon={superNextIcon ?? <Icon type={direction === 'rtl' ? 'uf-daoshouye' : 'uf-youjiantou_shuang'} />}
                prevIcon={prevIcon ?? <Icon type={direction === 'rtl' ? 'uf-arrow-right' : 'uf-arrow-left'} />}
                nextIcon={nextIcon ?? <Icon type={direction === 'rtl' ? 'uf-arrow-left' : 'uf-arrow-right'} />}
                // @ts-ignore
                diffValue={open && isHeaderSelect ? diffValue : undefined}
                headerSelectLeft={leftPanel}
                headerSelectRight={rightPanel}
                showSelectMask={open && isHeaderSelect ? maskShow : false}
                activeSelectPanel={activeSelectPanel}
                // @ts-ignore
                domKey={rangeKey}
            ></RCRangePicker>
        )
    }
)

const defaultProps = {
    size: 'md' as RangePickerProps['size'],
    antd: false,
    clearIcon: <Icon type='uf-close-c' />,
    suffixIcon: <Icon type='uf-calendar' />,
    timezone: 'Asia/Shanghai',
    serverTimezone: 'Asia/Shanghai',
    allowClear: true,
    autoFix: false,
    showToday: false,
    showRangeLabel: false,
    isHeaderSelect: true,
    atOnceFinish: false
}

RangePicker.defaultProps = defaultProps

export default RangePicker
